"use client";

import { useEffect, useRef, useState, type FC, type Ref } from "react";
import {
  Button,
  Card,
  Checkbox,
  Chip,
  Flex,
  Icon,
  Logo,
  Modal,
  ProgressBar,
  Text,
} from "@huub-web/ui";
import { mergeClasses } from "@huub-web/utils";
import { useTranslation } from "react-i18next";

import {
  cardContentStyle,
  documentContainerStyle,
  documentLogoStyle,
  documentStyle,
  documentSubtitleStyle,
  documentTitleStyle,
  documentIFrameStyle,
} from "./DocumentCard.css";

export interface DocumentCardProps {
  title?: string;
  description?: string;
  value?: string;
  signing?: boolean;
  download?: boolean;
  downloadLabel?: string;
  url?: string;
  documentTitle?: string;
  documentSubtitle?: string;
  acknowledgementLabel?: string;
  readLabel?: string;
  loading?: boolean;
  signed?: boolean;
  disabled?: boolean;
  variant?: "default" | "compact";
  onDownload?: () => void;
  onSign?: () => void;
}

export const DocumentCard: FC<
  DocumentCardProps & { ref?: Ref<HTMLDivElement> }
> = ({
  title,
  description,
  value: valueProp,
  signing,
  download,
  downloadLabel = "Download",
  url: urlProp,
  documentTitle,
  documentSubtitle,
  acknowledgementLabel,
  readLabel,
  loading = false,
  signed: signedProp = false,
  disabled = false,
  variant = "default",
  onDownload,
  onSign,
  ref,
}) => {
  const { t } = useTranslation();
  const value = valueProp ?? t("documentCard.viewAndSign");

  const [isChecked, setIsChecked] = useState(false);
  const [hasRead, setHasRead] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isSigned, setIsSigned] = useState(signedProp);

  const isSigningRef = useRef(false);

  const handleModalClose = () => {
    if ((loading || isChecked || isSigningRef.current) && isSigned === false) {
      return;
    }
    setIsModalOpen(false);
  };

  useEffect(() => {
    if (isChecked && !isSigningRef.current) {
      isSigningRef.current = true;
      onSign?.();
    }
  }, [isChecked, onSign]);

  useEffect(() => {
    if (signedProp) {
      setIsModalOpen(false);
      setIsSigned(true);
    }
  }, [signedProp]);

  const url = urlProp;
  // ? addParamsToURL(urlProp, { navpanes: "0" })
  // : null;

  return (
    <>
      {/* <pre>
          {JSON.stringify({ url, isSigned, signing, download }, null, 2)}
        </pre> */}

      <Card
        ref={ref}
        variant={isSigned ? "successSurface" : "infoSurface"}
        disabled={disabled}
        direction="horizontal"
        contentClassName={cardContentStyle}
        leading={
          variant === "default" && (
            <Flex
              className={mergeClasses(documentContainerStyle({ variant }))}
              grow
            >
              <Flex
                className={mergeClasses(documentStyle({ variant }))}
                direction="column"
                gap="xs"
                justify="space-between"
                align="flex-start"
                grow
              >
                <Logo size="s" className={documentLogoStyle} />
                <Flex direction="column" gap="xs">
                  <Text
                    variant="displayS"
                    color="currentColor75"
                    className={documentTitleStyle}
                  >
                    {documentTitle}
                  </Text>
                  <Text
                    variant="surfaceSubtitle"
                    color="currentColor50"
                    className={documentSubtitleStyle}
                  >
                    {documentSubtitle}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          )
        }
      >
        <Flex
          direction="column"
          gap={variant === "default" ? "l" : "m"}
          align="flex-start"
        >
          <Flex direction="column" gap={variant === "default" ? "s" : "xs"}>
            {title && (
              <Text as="h2" variant="displayS" bold>
                {title}
              </Text>
            )}
            {description && (
              <Text color="currentColor50" markdown>
                {description}
              </Text>
            )}
          </Flex>
          {signing && isSigned && (
            <Chip
              variant="successSurface"
              size="l"
              leading={<Icon icon="check" />}
            >
              {t("documentCard.signed")}
            </Chip>
          )}
          {value && signing && !isSigned && (
            <Button
              variant="primary"
              size="s"
              disabled={disabled}
              onClick={() => {
                setIsModalOpen(true);
              }}
              leading={<Icon icon="signature" size="s2" />}
            >
              {value}
            </Button>
          )}
          {download && downloadLabel && !signing && (
            <Button
              variant="primary"
              size="s"
              onClick={onDownload}
              alignSelf="flex-start"
              leading={<Icon icon="downloadFilled" size="s2" />}
            >
              {downloadLabel}
            </Button>
          )}
          {isSigned && signing && (
            <Button
              variant="success"
              size="s"
              onClick={onDownload}
              leading={<Icon icon="downloadFilled" size="s2" />}
            >
              {downloadLabel}
            </Button>
          )}
        </Flex>
      </Card>

      {isModalOpen && (
        <Modal
          escape
          closeable
          onClose={handleModalClose}
          title={title}
          closeDisabled={(loading || isChecked) && isSigned === false}
          trailing={
            loading && <ProgressBar variant="infinite" position="absolute" />
          }
        >
          {url && (
            <iframe
              src={url}
              className={documentIFrameStyle}
              title={documentTitle}
            />
          )}
          {download && !url && (
            <Button
              variant="primary"
              size="s"
              onClick={onDownload}
              alignSelf="flex-start"
              leading={<Icon icon="downloadFilled" size="s2" />}
            >
              {downloadLabel}
            </Button>
          )}
          <Checkbox
            checked={hasRead}
            onChange={() => setHasRead(!hasRead)}
            label={readLabel}
            disabled={isChecked}
          />
          <Checkbox
            checked={isChecked}
            onChange={() => setIsChecked(true)}
            disabled={isChecked || !hasRead}
            label={acknowledgementLabel}
          />
        </Modal>
      )}
    </>
  );
};
